import { defineStore } from 'pinia'
import { usePursuitsStore } from '@/stores/pursuits'
import { useNotificationsStore } from '@/stores/notifications'
import { useOwnershipStore } from '@/stores/ownership'
import { useToast } from '@/composables/useToast'
import type {
  ActionRequiredEvent,
  CatalogSyncedEvent,
  WorkflowProgressEvent,
} from '@/lib/types'

const SSE_URL = '/api/v1/orgs/current/events'
const POLL_INTERVAL_MS = 20_000
const EVENT_NAMES = [
  'pursuit.updated',
  'pursuits.bulk',
  'action.required',
  'catalog.synced',
  'workflow.progress',
  'ownership.requested',
  'ownership.resolved',
] as const

// EventSource + timers live at module level: they are imperative resources,
// not reactive state (wrapping them in Pinia's reactive proxy breaks close()).
let source: EventSource | null = null
let pollTimer: ReturnType<typeof setInterval> | null = null

// Runs-aware progress handlers: pages (e.g. the pursuit detail Workflow
// section) register here to receive live workflow.progress events. Module
// level for the same reason as `source` — no reactivity needed.
type WorkflowProgressHandler = (e: WorkflowProgressEvent) => void
const progressHandlers = new Set<WorkflowProgressHandler>()

/** Subscribe to SSE workflow.progress events; returns an unsubscribe fn. */
export function onWorkflowProgress(handler: WorkflowProgressHandler): () => void {
  progressHandlers.add(handler)
  return () => progressHandlers.delete(handler)
}

export const useEventsStore = defineStore('events', {
  state: () => ({
    orgId: null as string | null,
    connected: false,
    polling: false,
  }),

  actions: {
    /** (Re)connect for an org. Tears down any previous org's stream first. */
    start(orgId: string) {
      if (this.orgId === orgId && source) return
      this.stop()
      this.orgId = orgId

      // Org travels as a query param — EventSource can't set X-Org-ID.
      const es = new EventSource(`${SSE_URL}?org=${encodeURIComponent(orgId)}`)
      source = es

      es.onopen = () => {
        this.connected = true
        this.stopPolling()
      }
      es.onerror = () => {
        // EventSource auto-reconnects; keep pursuits fresh via polling while
        // the stream is down.
        this.connected = false
        this.startPolling()
      }
      for (const name of EVENT_NAMES) {
        es.addEventListener(name, (e) => this.handle(name, (e as MessageEvent).data))
      }
    },

    /** Full teardown — call on org switch or layout unmount. */
    stop() {
      source?.close()
      source = null
      this.connected = false
      this.orgId = null
      this.stopPolling()
    },

    startPolling() {
      if (pollTimer) return
      this.polling = true
      pollTimer = setInterval(() => {
        const pursuits = usePursuitsStore()
        if (pursuits.loaded) pursuits.fetchAll()
      }, POLL_INTERVAL_MS)
    },

    stopPolling() {
      if (pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
      }
      this.polling = false
    },

    handle(name: (typeof EVENT_NAMES)[number], raw: string) {
      let payload: { type: string; data: unknown }
      try {
        payload = JSON.parse(raw)
      } catch {
        return
      }
      const pursuits = usePursuitsStore()
      const notifications = useNotificationsStore()
      const toast = useToast()

      switch (name) {
        case 'pursuit.updated':
          pursuits.applyEvent(payload)
          break
        // A batch carries a count, not ids: refetching the board once beats
        // eighty fetchOne calls, which is what per-pursuit events would cost
        // every open tab in the org.
        case 'pursuits.bulk':
          if (pursuits.loaded) pursuits.fetchAll('open')
          break
        case 'action.required': {
          const data = payload.data as ActionRequiredEvent
          pursuits.applyEvent(payload)
          notifications.fetch()
          if (data?.message) toast.info(data.message)
          break
        }
        case 'catalog.synced': {
          const data = payload.data as CatalogSyncedEvent
          if (data && (data.inserted || data.updated || data.changes)) {
            toast.info(
              `Catalog synced — ${data.inserted ?? 0} new, ${data.updated ?? 0} updated, ${data.changes ?? 0} changes`,
            )
          }
          break
        }
        case 'workflow.progress': {
          const data = payload.data as WorkflowProgressEvent
          if (!data?.pursuit_id) break
          pursuits.applyRunProgress(data)
          for (const handler of progressHandlers) handler(data)
          break
        }
        // The stream is org-wide, so everyone's browser hears about every
        // hand-off. The store decides whose prompt it is (and whose speaker
        // rings) by comparing owner_member_id to the viewer.
        case 'ownership.requested': {
          const ownership = useOwnershipStore()
          ownership.fetch(true)
          notifications.fetch()
          break
        }
        case 'ownership.resolved': {
          useOwnershipStore().fetch()
          notifications.fetch()
          pursuits.applyEvent(payload)
          break
        }
      }
    },
  },
})
